import { createAsyncThunk } from '@reduxjs/toolkit';
import { AiChatConversationResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatConversationResponse';
import { AiChatConversationSummaryResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatConversationSummaryResponse';
import type { AiChatAttachmentResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatAttachmentsUploadResponse';
import type { AiChatModelMode } from '@payer-admin-ui/api/src/ai/requests/AiChatRequest';
import type { PayerErrorLike } from '@payer-admin-ui/components-v2/src/error/PayerErrorMessage';
import { toPayerErrorLike } from '@payer-admin-ui/components-v2/src/error/toPayerErrorLike';
import { aiAPI } from '../../redux/aiAPI';
import type { MerchantAdminState } from '../../redux/store';
import { appendStreamingServerMessage } from './aiChatActions';

export const listAiChatConversations = createAsyncThunk<AiChatConversationSummaryResponse[], { limit?: number }>(
  'ai/chat/history/list',
  async ({ limit = 50 }) => {
    return aiAPI.chat.listConversations(limit);
  },
);

export const fetchAiChatConversation = createAsyncThunk<
  AiChatConversationResponse,
  { conversationId: string },
  { state: MerchantAdminState }
>(
  'ai/chat/history/get',
  async ({ conversationId }) => {
    return aiAPI.chat.getConversation(conversationId);
  },
  {
    condition: ({ conversationId }, { getState }) => {
      const conversation = getState().ai.chat.entities[conversationId];
      if (!conversation) return true;
      return !conversation.isEphemeral;
    },
  },
);

export const sendAiChat = createAsyncThunk<
  { conversationId: string; response: Awaited<ReturnType<typeof aiAPI.chat.chat>>; requestId: string },
  { conversationId: string; message: string; modelMode: AiChatModelMode },
  { state: MerchantAdminState }
>('ai/chat/send', async ({ conversationId, message, modelMode }, { dispatch, getState, requestId, signal }) => {
  let messageIndex = 0;
  const attachmentIds = getState().ai.chat.entities[conversationId]?.attachmentIds ?? [];
  const response = await aiAPI.chat.chat(
    {
      message,
      conversationId,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : null,
      modelMode,
    },
    {
      signal,
      handlers: {
        onMessage: event => {
          dispatch(
            appendStreamingServerMessage({
              conversationId,
              requestId,
              messageIndex,
              message: event.message,
            }),
          );
          messageIndex += 1;
        },
      },
    },
  );
  return { conversationId, response, requestId };
});

export const retryAiChat = createAsyncThunk<
  { conversationId: string; response: Awaited<ReturnType<typeof aiAPI.chat.chat>>; requestId: string },
  { conversationId: string },
  { state: MerchantAdminState }
>('ai/chat/retry', async ({ conversationId }, { dispatch, getState, requestId, signal }) => {
  const conversation = getState().ai.chat.entities[conversationId];
  const message = conversation?.lastUserMessage;
  if (!message) throw new Error('No user message to retry');

  let messageIndex = 0;
  const attachmentIds = conversation?.attachmentIds ?? [];
  const resolvedModelMode = conversation?.lastModelMode ?? 'FAST';
  const response = await aiAPI.chat.chat(
    {
      message,
      conversationId,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : null,
      modelMode: resolvedModelMode,
    },
    {
      signal,
      handlers: {
        onMessage: event => {
          dispatch(
            appendStreamingServerMessage({
              conversationId,
              requestId,
              messageIndex,
              message: event.message,
            }),
          );
          messageIndex += 1;
        },
      },
    },
  );
  return { conversationId, response, requestId };
});

export const deleteAiChatAttachment = createAsyncThunk<
  { conversationId: string; attachmentId: string },
  { conversationId: string; attachmentId: string },
  { state: MerchantAdminState; rejectValue: PayerErrorLike }
>(
  'ai/chat/attachments/delete',
  async ({ attachmentId, conversationId }, { rejectWithValue }) => {
    try {
      await aiAPI.chat.deleteAttachment(attachmentId);
      return { conversationId, attachmentId };
    } catch (error) {
      return rejectWithValue(toPayerErrorLike(error));
    }
  },
  {
    condition: ({ conversationId, attachmentId }, { getState }) => {
      const conversation = getState().ai.chat.entities[conversationId];
      if (!conversation) return true;
      return !conversation.deletingAttachmentIds.includes(attachmentId);
    },
  },
);

export const uploadAiChatAttachments = createAsyncThunk<
  { conversationId: string; attachments: AiChatAttachmentResponse[] },
  { conversationId: string; files: File[] },
  { state: MerchantAdminState; rejectValue: PayerErrorLike }
>(
  'ai/chat/attachments/upload',
  async ({ conversationId, files }, { rejectWithValue }) => {
    try {
      const response = await aiAPI.chat.uploadAttachments({ conversationId, files });
      return { conversationId, attachments: response.attachments };
    } catch (error) {
      return rejectWithValue(toPayerErrorLike(error));
    }
  },
  {
    condition: ({ conversationId, files }, { getState }) => {
      if (files.length === 0) return false;
      const conversation = getState().ai.chat.entities[conversationId];
      if (!conversation) return true;
      return !conversation.isUploadingAttachments;
    },
  },
);
